import { customAlphabet } from "nanoid";

const SHORT_ID_LENGTH = 9;

type ThreeLetters<S extends string> =
  S extends `${string}${string}${string}${infer R}`
    ? R extends ""
      ? S
      : never
    : never;

type UniqueValues<T extends Record<string, string>> = {
  [K in keyof T]: T[K] extends T[Exclude<keyof T, K>]
    ? never
    : ThreeLetters<T[K]>;
};

export const ID_PREFIXES = (<const T extends Record<string, string>>(
  p: T & UniqueValues<T>,
) => p)({
  // core
  team: "org",
  teamMember: "mem",
  teamRole: "rle",
  project: "prj",
  generationV2: "gv2",
  model: "mdl",
  modelEngineConfig: "cfg",
  modelEngineConfigNote: "cfn",
  modelAccessRequest: "mar",

  // deployments
  airTrafficControl: "atc",
  controlTower: "dct",
  controlTowerNode: "ctn",
  controlTowerDevice: "ctd",
  controlTowerEvent: "cte",
  endpoint: "enp",
  endpointEvent: "epe",
  deployment: "dpl",
  deploymentInstance: "ins",
  deploymentEvent: "dpe",
  deploymentScaleBatch: "dsb",
  fleetGpuInstance: "gpi",
  fleetGpu: "gpu",

  // signals
  signalAlert: "sal",
  signalAlertEvent: "sae",

  // inference analysis
  inferenceAnalysisRun: "iar",
  insight: "isg",
  analysisSetting: "ast",

  // backfills
  pricingBackfillJob: "pbf",

  // home-page recommendations
  recommendation: "rcm",
  projectCtaOverride: "cta",
});

const alphanumericOnlyNanoId = customAlphabet(
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
);

function nanoId(prefix?: keyof typeof ID_PREFIXES) {
  return prefix
    ? `${ID_PREFIXES[prefix]}_${alphanumericOnlyNanoId(21)}`
    : alphanumericOnlyNanoId(21);
}

function uuidV4() {
  return crypto.randomUUID();
}

function truncateId(id: string, lengthToTruncate = SHORT_ID_LENGTH): string {
  return id.length <= lengthToTruncate
    ? id
    : `${id.slice(0, lengthToTruncate)}...`;
}

function shortId(lengthToTruncate = SHORT_ID_LENGTH) {
  return nanoId().slice(0, lengthToTruncate).toLowerCase();
}

function shortIdAlphaNumeric(lengthToTruncate = SHORT_ID_LENGTH) {
  return alphanumericOnlyNanoId(lengthToTruncate);
}

export const ids = {
  nanoId,
  uuidV4,
  truncateId,
  shortId,
  shortIdAlphaNumeric,
};
